/**
 * Checks if an error message/text indicates an Apollo in-flight cancellation
 * (such as invariant error 92 "Store reset while query was in flight" or error 90 "QueryManager stopped while query was in flight").
 */
const isApolloInFlightCancelError = (text: string): boolean => {
  if (!text) return false;
  const lower = text.toLowerCase();
  if (
    lower.includes("store reset while query was in flight") ||
    lower.includes("querymanager stopped while query was in flight") ||
    lower.includes("while query was in flight")
  ) {
    return true;
  }

  if (lower.includes("go.apollo.dev/c/err")) {
    if (
      lower.includes("%22message%22%3a92") ||
      lower.includes("%22message%22%3a90") ||
      lower.includes('"message":92') ||
      lower.includes('"message": 92') ||
      lower.includes('"message":90') ||
      lower.includes('"message": 90')
    ) {
      return true;
    }
    try {
      const decoded = decodeURIComponent(lower);
      if (
        decoded.includes('"message":92') ||
        decoded.includes('"message": 92') ||
        decoded.includes('"message":90') ||
        decoded.includes('"message": 90')
      ) {
        return true;
      }
    } catch {
      // Ignore URI malformed error
    }
  }

  return false;
};

/**
 * Checks if an error or rejection is caused by an aborted / cancelled request
 * (such as when switching pages, logging out, or unmounting components while a GraphQL query is in-flight).
 */
export const isAbortedRequestError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof error === "string") {
    const lower = error.toLowerCase();
    return (
      lower.includes("operation was aborted") ||
      lower.includes("aborterror") ||
      lower.includes("user aborted") ||
      lower.includes("err_canceled") ||
      lower.includes("canceled") ||
      lower.includes("cancelled") ||
      isApolloInFlightCancelError(error)
    );
  }

  const err = error as Record<string, any>;

  const indicators = [
    err.name,
    err.code,
    err.message,
    err.stack,
    err.reason?.name,
    err.reason?.code,
    err.reason?.message,
    err.reason?.stack,
    err.cause?.name,
    err.cause?.code,
    err.cause?.message,
    err.cause?.stack,
    err.networkError?.name,
    err.networkError?.code,
    err.networkError?.message,
    err.networkError?.result?.message,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    indicators.includes("aborterror") ||
    indicators.includes("operation was aborted") ||
    indicators.includes("user aborted") ||
    indicators.includes("err_canceled") ||
    indicators.includes("canceled") ||
    indicators.includes("cancelled") ||
    err.name === "AbortError" ||
    err.code === 20 ||
    err.networkError?.name === "AbortError" ||
    err.networkError?.code === 20 ||
    isApolloInFlightCancelError(indicators)
  );
};
